import { useContext, useEffect } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { logo } from "../../../assets";
import {
  confirmForgetPasswordUserValidation,
  forgetPasswordUserValidation,
} from "../../../utils/validation/validation";
import SiteTitle from "../../../helpers/SiteTitle/SiteTitle";
import ValidationErrorMessage from "../../../helpers/ValidationErrorMessage/ValidationErrorMessage";
import { authContext } from "../../../context/AuthContext/AuthContext";
import { HiEye, HiEyeOff } from "react-icons/hi";

const ForgetPassword = () => {
  const {
    isShowPassForgetPass,
    setIsShowPassForgetPass,
    isShowPassForgetPassConfirm,
    setIsShowPassForgetPassConfirm,
    phonenumber,
    isReceiveCode,
    handleForgetPassword,
    handleConfirmForgetPassword,
    setDeferredPrompt,
    showInstallButton,
    setShowInstallButton,
    handleInstallClick,
  } = useContext(authContext);

  // Form 1 - Request Password Reset Code
  const {
    register: registerForm1,
    handleSubmit: handleSubmitForm1,
    formState: { errors: errorsForm1 },
  } = useForm({
    resolver: yupResolver(forgetPasswordUserValidation),
  });

  // Form 2 - Confirm New Password
  const {
    register: registerForm2,
    handleSubmit: handleSubmitForm2,
    formState: { errors: errorsForm2 },
  } = useForm({
    resolver: yupResolver(confirmForgetPasswordUserValidation),
  });

  // PWA Installer
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, []);

  return (
    <>
      <SiteTitle title="تغییر رمز عبور" />
      <div className="bg-[#101632] min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md mx-auto">
          <div className="bg-[#153477] backdrop-blur-sm rounded-xl shadow-lg overflow-hidden border border-gray-200 transition-all duration-300 hover:scale-[1.01]">
            {/* Logo Header */}
            <div className="bg-[#153477] py-6 flex flex-col items-center border-b border-gray-200">
              <img
                src={logo}
                alt="Website Logo"
                width={80}
                className="border-2 border-yellow-500 rounded-full p-1 bg-white shadow-sm"
              />
              <h2 className="text-2xl font-bold text-white mt-4">
                تغییر رمز عبور
              </h2>
              <p className="text-gray-500 text-sm mt-1">شادمهر</p>
            </div>

            <div className="p-6">
              {!isReceiveCode ? (
                <form
                  onSubmit={handleSubmitForm1(handleForgetPassword)}
                  className="space-y-4"
                >
                  {/* Phone Number */}
                  <div>
                    <label
                      htmlFor="phonenumber"
                      className="block text-sm font-medium text-black mb-2"
                    >
                      شماره موبایل
                    </label>
                    <input
                      type="number"
                      name="phonenumber"
                      placeholder="09123456789"
                      autoComplete="off"
                      {...registerForm1("phonenumber")}
                      className="w-full px-4 py-3 bg-[#171d3f] border border-gray-300 rounded-lg 
                        text-white placeholder-gray-400 focus:outline-none focus:ring-2 
                        focus:ring-yellow-500 focus:border-transparent"
                    />
                    <ValidationErrorMessage
                      message={errorsForm1.phonenumber?.message}
                    />
                  </div>
                  {/* Submit Button */}
                  <button
                    type="submit"
                    className="w-full py-3 bg-[#153477] hover:bg-yellow-600 text-white border border-gray-300 text-white font-medium 
                      rounded-lg transition duration-200 shadow-md"
                  >
                    دریافت کد
                  </button>
                </form>
              ) : (
                <form
                  onSubmit={handleSubmitForm2(handleConfirmForgetPassword)}
                  className="space-y-4"
                >
                  {/* Phone Number (Read Only) */}
                  <div>
                    <label
                      htmlFor="phoneNumber"
                      className="block text-sm font-medium text-black mb-2"
                    >
                      شماره موبایل
                    </label>
                    <input
                      type="number"
                      name="phoneNumber"
                      readOnly
                      value={phonenumber}
                      className="w-full px-4 py-3 bg-[#171d3f] border border-gray-300 rounded-lg 
                        text-white placeholder-gray-400 focus:outline-none focus:ring-2 
                        focus:ring-yellow-500 focus:border-transparent"
                    />
                  </div>
                  {/* New Password */}
                  <div>
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium text-black mb-2"
                    >
                      رمز عبور جدید
                    </label>
                    <div className="relative">
                      <input
                        type={isShowPassForgetPass ? "text" : "password"}
                        name="password"
                        placeholder="••••••"
                        autoComplete="off"
                        {...registerForm2("password")}
                        className="w-full px-4 py-3 bg-[#171d3f] border border-gray-300 rounded-lg 
                          text-white placeholder-gray-400 focus:outline-none focus:ring-2 
                          focus:ring-yellow-500 focus:border-transparent pr-12"
                      />
                      <button
                        type="button"
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-yellow-600"
                        onClick={() =>
                          setIsShowPassForgetPass(!isShowPassForgetPass)
                        }
                      >
                        {isShowPassForgetPass ? (
                          <HiEye size={20} className="text-gray-500" />
                        ) : (
                          <HiEyeOff size={20} className="text-gray-500" />
                        )}
                      </button>
                    </div>
                    <ValidationErrorMessage
                      message={errorsForm2.password?.message}
                    />
                  </div>
                  {/* Confirm Password */}
                  <div>
                    <label
                      htmlFor="confirmpassword"
                      className="block text-sm font-medium text-black mb-2"
                    >
                      تکرار رمز عبور جدید
                    </label>
                    <div className="relative">
                      <input
                        type={isShowPassForgetPassConfirm ? "text" : "password"}
                        name="confirmpassword"
                        placeholder="••••••"
                        autoComplete="off"
                        {...registerForm2("confirmpassword")}
                        className="w-full px-4 py-3 bg-[#171d3f] border border-gray-300 rounded-lg 
                          text-white placeholder-gray-400 focus:outline-none focus:ring-2 
                          focus:ring-yellow-500 focus:border-transparent pr-12"
                      />
                      <button
                        type="button"
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-yellow-600"
                        onClick={() =>
                          setIsShowPassForgetPassConfirm(
                            !isShowPassForgetPassConfirm
                          )
                        }
                      >
                        {isShowPassForgetPassConfirm ? (
                          <HiEye size={20} className="text-gray-500" />
                        ) : (
                          <HiEyeOff size={20} className="text-gray-500" />
                        )}
                      </button>
                    </div>
                    <ValidationErrorMessage
                      message={errorsForm2.confirmpassword?.message}
                    />
                  </div>
                  {/* Verification Code */}
                  <div>
                    <label
                      htmlFor="token"
                      className="block text-sm font-medium text-black mb-2"
                    >
                      کد تأیید
                    </label>
                    <input
                      type="number"
                      name="token"
                      placeholder="*****"
                      autoComplete="off"
                      {...registerForm2("token")}
                      className="w-full px-4 py-3 bg-[#171d3f] border border-gray-300 rounded-lg 
                        text-white placeholder-gray-400 focus:outline-none focus:ring-2 
                        focus:ring-yellow-500 focus:border-transparent"
                    />
                    <ValidationErrorMessage
                      message={errorsForm2.token?.message}
                    />
                  </div>
                  {/* Timer */}
                  <p className="text-center text-gray-600 text-sm mt-2">
                    زمان باقی مانده:{" "}
                    <span
                      id="verificationCodeCountDown"
                      className="text-yellow-600 font-medium"
                    >
                      120
                    </span>{" "}
                    ثانیه
                  </p>
                  {/* Submit Button */}
                  <button
                    type="submit"
                    className="w-full py-3 bg-[#153477] hover:bg-yellow-600 text-white border border-gray-300 text-white font-medium 
                      rounded-lg transition duration-200 shadow-md"
                  >
                    تغییر رمز عبور
                  </button>
                </form>
              )}

              {/* Back to Login Link */}
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600">
                  به حساب خود بازگردید؟{" "}
                  <Link
                    to="/"
                    className="text-yellow-600 hover:text-yellow-700 font-medium underline"
                  >
                    ورود
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ForgetPassword;
